'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AppleIcon from '@mui/icons-material/Apple';
import AdbIcon from '@mui/icons-material/Adb';
import WindowIcon from '@mui/icons-material/Window';
import {theme} from "@/styles/theme";

const osTypes = [
  { 
      name: 'Windows', 
      icon: <WindowIcon />, 
      screenshots: {
          reset: ['/troubleshoot/windows-fix-1.png', '/troubleshoot/windows-fix-2.png']
      }
  },
  { 
      name: 'Apple', 
      icon: <AppleIcon />, 
      screenshots: {
          reset: ['/troubleshoot/ios-fix-1.png', '/troubleshoot/ios-fix-2.png', '/troubleshoot/ios-fix-3.png']
      }
  },
  { 
      name: 'Android', 
      icon: <AdbIcon />, 
      screenshots: {
          reset: ['/troubleshoot/android-fix-1.png', '/troubleshoot/android-fix-2.png', '/troubleshoot/android-fix-3.png']
      }
  }
];

interface TroubleshootViewProps {
    onBack: () => void;
}

export default function TroubleshootView({ onBack }: TroubleshootViewProps) {
  const [osIndex, setOsIndex] = React.useState(0);
  const [modalOpen, setModalOpen] = React.useState(false);

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);

  const handleOsSelect = (index: number) => {
    setOsIndex(index);
    handleModalClose();
  };

  const selectedOS = osTypes[osIndex].name;
  const screenshots = osTypes[osIndex].screenshots;

  const HEADER_HEIGHT = 72;
  const BOTTOM_MARGIN = 16+16;

  // Стиль для карточек с инструкциями
  const cardStyle = {
      background: "linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
      backdropFilter: "blur(10px)",
      p: 2, 
      borderRadius: 1, 
      border: '1px solid rgba(255, 255, 255, 0.12)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
  };

  return (
    <>
      <Box sx={{ 
          position: 'fixed', 
          inset: 0, 
          background: "linear-gradient(rgb(10, 10, 10) 0%, rgb(26, 26, 26) 50%, rgb(10, 10, 10) 100%)",
          zIndex: 9999,
          overflow: 'hidden',
          overscrollBehavior: 'none',
          height: '100dvh',
        }}
      >
        {/* Header */}
        <Box sx={{
          height: `${HEADER_HEIGHT}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'transparent',
          zIndex: 1001,
          my: 1
        }}>
          <Button 
            onClick={handleModalOpen} 
            endIcon={<ArrowDropDownIcon />}
            sx={{
              fontWeight: 'bold',
              fontSize: '1rem',
              color: 'text.primary',
              textTransform: 'none',
              // Стили кнопки выбора ОС
              background: "rgba(255, 255, 255, 0.05)",
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 50, // Pill shape
              px: 2,
              '&:hover': {
                  background: "rgba(255, 255, 255, 0.1)",
              }
            }}
          >
            {selectedOS}
          </Button>
          <IconButton onClick={onBack} sx={{backgroundColor:theme.palette.background.paper}}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ 
            height: `calc(100dvh - ${HEADER_HEIGHT}px - ${BOTTOM_MARGIN}px)`,
            overflowY: 'auto',
            maxWidth: 800, 
            mx: 'auto', 
            width: 'calc(100% - 32px)', 
            
            // Обновленный стиль контейнера
            backgroundImage: "linear-gradient(to bottom right, #262626e6, #171717b3)",
            backdropFilter: "blur(10px)",
            border: '1px solid',
            borderColor: 'divider',
            
            borderRadius: 1, 
            p: 3,
            
            '&::-webkit-scrollbar': {
                width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '3px',
            }
        }}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
                Проблемы с подключением?
            </Typography>

            <Stack spacing={3}>
              {/* Step 1 */}
              <Box sx={cardStyle}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  1. Обновите подписку
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Отключите VPN, обновите данные подписки — возможно, не сработало автообновление. Убедитесь, что перед подключением выбран нужный сервер.
                </Typography>
              </Box>

              {/* Step 2 */}
              <Box sx={cardStyle}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  2. Сбросьте настройки туннеля
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Отключите VPN, зайдите в настройки. Пролистайте вниз и нажмите «Сброс». Выберите «Сброс настроек туннеля».
                </Typography>
                <Stack spacing={2}>
                    {screenshots.reset.map((src, index) => (
                        <Box 
                            key={src}
                            component="img"
                            src={src}
                            alt={`Reset screenshot ${index + 1}`}
                            sx={{ 
                                width: '100%', 
                                height: 'auto', 
                                borderRadius: 1, 
                                border: '1px solid', 
                                borderColor: 'divider',
                            }} 
                        />
                    ))}
                </Stack>
              </Box>

              {/* Step 3 */}
              <Box sx={cardStyle}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  3. Обновите приложение и перезагрузите устройство
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Проверьте наличие обновлений в магазине приложений. Затем перезагрузите устройство.
                </Typography>
              </Box>

              {/* Step 4 */}
              <Box sx={cardStyle}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  4. Проверьте память
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Убедитесь, что на устройстве достаточно свободной памяти. При необходимости удалите ненужные файлы.
                </Typography>
              </Box>

              {/* Step 5 */}
              <Box sx={cardStyle}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  5. Обратитесь в поддержку
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Если ни один из пунктов не помог — напишите нам в поддержку.
                </Typography>
              </Box>
            </Stack>
        </Box>
      </Box>
      
      {/* OS Selection Modal */}
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby="os-selection-modal-title"
        BackdropProps={{
            sx: {
                backdropFilter: 'blur(3px)',
            }
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          backgroundImage: "linear-gradient(to bottom right, #262626e6, #171717b3)",
          backdropFilter: "blur(10px)",
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 24,
          p: 2,
          borderRadius: 1, 
        }}>
          <Typography id="os-selection-modal-title" variant="h6" component="h2" sx={{ mb: 2, textAlign: 'center' }}>
            Выберете устройство
          </Typography>
          <Stack spacing={1}>
            {osTypes.map((os, index) => {
                const isActive = index === osIndex;
                return (
                    <Paper
                        key={os.name}
                        onClick={() => handleOsSelect(index)}
                        elevation={0}
                        sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            background: isActive 
                                ? "rgba(255, 94, 0, 0.15)" 
                                : "linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
                            border: '1px solid',
                            borderColor: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.12)',
                            borderRadius: 1, 
                            width: '100%',
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: 'primary.light',
                                background: isActive 
                                    ? "rgba(255, 94, 0, 0.25)" 
                                    : "linear-gradient(145deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.05) 100%)",
                            }
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: isActive ? 'primary.main' : 'text.primary' }}>{os.name}</Typography>
                        {React.cloneElement(os.icon, { sx: { color: isActive ? 'primary.main' : 'text.secondary' } })}
                    </Paper>
                );
            })}
          </Stack>
        </Box>
      </Modal>
    </>
  );
}
